import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { MessageSquare, Send, Loader2, X, Mail } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUPPORT_EMAIL = 'support@support.pipeguide.tech';

export function SupportButton() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen(true)}
        className="rounded-md text-zinc-500 hover:text-zinc-700"
        data-testid="support-btn"
      >
        <MessageSquare className="w-4 h-4 mr-1.5" />
        Support
      </Button>

      {open && <SupportModal onClose={() => setOpen(false)} />}
    </>
  );
}

export function SupportFooter() {
  return (
    <a
      href={`mailto:${SUPPORT_EMAIL}?subject=PipeGuide Support`}
      className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-brand-primary transition-colors"
      data-testid="support-mailto-link"
    >
      <Mail className="w-3 h-3" />
      {SUPPORT_EMAIL}
    </a>
  );
}

function SupportModal({ onClose }) {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState('general');
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setSending(true);
    try {
      const { data } = await axios.post(`${API}/support/submit`, {
        subject: subject.trim(),
        message: message.trim(),
        category,
      });

      toast.success(data.message || 'Support request submitted!');
      onClose();
    } catch (e) {
      toast.error('Failed to submit. Try emailing us directly.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="support-modal">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100">
          <div>
            <h2 className="text-lg font-semibold text-zinc-900" style={{ fontFamily: 'var(--font-heading)' }}>Get Help</h2>
            <p className="text-xs text-zinc-500 mt-0.5">We typically respond within a few hours</p>
          </div>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-zinc-100 text-zinc-400" data-testid="support-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="support-category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General Question</SelectItem>
                <SelectItem value="bug">Bug Report</SelectItem>
                <SelectItem value="feature">Feature Request</SelectItem>
                <SelectItem value="billing">Billing Issue</SelectItem>
                <SelectItem value="integration">Integration Help</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Subject</Label>
            <Input
              placeholder="Brief description of your issue"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="rounded-md"
              data-testid="support-subject-input"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Message</Label>
            <textarea
              placeholder="Please describe your issue in detail..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              className="w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent resize-none"
              data-testid="support-message-input"
            />
          </div>

          <div className="flex items-center justify-between pt-2">
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=PipeGuide Support`}
              className="text-xs text-zinc-400 hover:text-brand-primary flex items-center gap-1 transition-colors"
              data-testid="support-email-link"
            >
              <Mail className="w-3 h-3" />
              Or email us directly
            </a>
            <Button
              type="submit"
              disabled={sending || !subject.trim() || !message.trim()}
              className="rounded-md bg-brand-primary hover:bg-brand-hover"
              data-testid="support-submit-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
              {sending ? 'Sending...' : 'Submit'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
